import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import logo from "../../assets/usjp-logo__1_-removebg-preview.png";
import AuthShell from "../../components/auth/AuthShell";
import { useAuth } from "../../hooks/useAuth";
import { systemChangePassword } from "../../services/systemAuthService";

// Forced first-login step for a system user still on a temporary password
// (must_change_password: true) — reached either straight after AdminLogin's
// redirect, or via ProtectedRoute bouncing any /admin/* URL back here while
// that flag is still set. Shares AdminLogin's visual language (AuthShell,
// same branded card, same input styling).
export default function ChangePassword() {
  const navigate = useNavigate();
  const { updateUser, logout } = useAuth();

  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const fieldError = (key) => {
    const msgs = fieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  const inputBase =
    "w-full border rounded-xl px-4 py-4 text-lg focus:outline-none focus:ring-2 transition";
  const fieldClass = (key) =>
    `${inputBase} ${
      fieldError(key)
        ? "border-red-400 focus:ring-red-400 bg-red-50"
        : "border-gray-300 focus:ring-blue-500"
    }`;

  const validate = () => {
    const errs = {};
    if (!currentPassword) errs.current_password = ["Enter your temporary password."];
    if (!password) errs.password = ["Enter a new password."];
    else if (password.length < 8) errs.password = ["Password must be at least 8 characters."];
    else if (currentPassword && password === currentPassword)
      errs.password = ["New password must be different from the current one."];
    if (!passwordConfirmation) errs.password_confirmation = ["Confirm your new password."];
    else if (password && passwordConfirmation !== password)
      errs.password_confirmation = ["Passwords do not match."];
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setError("");
    setSubmitting(true);
    try {
      const user = await systemChangePassword({ currentPassword, password, passwordConfirmation });
      updateUser(user);
      toast.success("Password changed successfully.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) {
        setFieldErrors(data.errors);
        setError(data.message || "Please correct the highlighted fields.");
      } else {
        setError(data?.message || "Could not change your password. Please try again.");
      }
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <AuthShell>
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 lg:p-12 w-full">
          <div className="flex items-center gap-3 sm:gap-5 mb-6 sm:mb-8">
            <img src={logo} alt="USJ Logo" className="w-16 h-16 sm:w-24 sm:h-24 object-contain flex-shrink-0" />
            <div>
              <p className="font-semibold text-gray-800 text-sm sm:text-xl leading-tight whitespace-nowrap">
                University of Sri Jayewardenepura
              </p>
              <p className="text-sm sm:text-lg text-blue-600">Sports Facility — Admin Portal</p>
            </div>
          </div>

          <h2 className="text-center text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Change Your Password</h2>
          <p className="text-center text-gray-500 text-lg mb-8">
            You're signed in with a temporary password. Set a new password to continue.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <label htmlFor="current-password" className="block text-lg font-medium text-gray-700 mb-2">
              Temporary / Current Password
            </label>
            <input
              id="current-password"
              type={showPasswords ? "text" : "password"}
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setFieldErrors((fe) => (fe.current_password ? { ...fe, current_password: null } : fe));
              }}
              placeholder="••••••••"
              className={`${fieldClass("current_password")} mb-1`}
            />
            {fieldError("current_password") && (
              <p className="text-red-500 text-sm mb-3">{fieldError("current_password")}</p>
            )}

            <label htmlFor="new-password" className="block text-lg font-medium text-gray-700 mb-2 mt-4">
              New Password <span className="text-gray-400 font-normal text-base">(min 8 characters)</span>
            </label>
            <input
              id="new-password"
              type={showPasswords ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFieldErrors((fe) => (fe.password ? { ...fe, password: null } : fe));
              }}
              placeholder="••••••••"
              className={`${fieldClass("password")} mb-1`}
            />
            {fieldError("password") && <p className="text-red-500 text-sm mb-3">{fieldError("password")}</p>}

            <label htmlFor="confirm-password" className="block text-lg font-medium text-gray-700 mb-2 mt-4">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                id="confirm-password"
                type={showPasswords ? "text" : "password"}
                autoComplete="new-password"
                value={passwordConfirmation}
                onChange={(e) => {
                  setPasswordConfirmation(e.target.value);
                  setFieldErrors((fe) => (fe.password_confirmation ? { ...fe, password_confirmation: null } : fe));
                }}
                placeholder="••••••••"
                className={`${fieldClass("password_confirmation")} pr-16`}
              />
              <button
                type="button"
                onClick={() => setShowPasswords((s) => !s)}
                aria-label={showPasswords ? "Hide passwords" : "Show passwords"}
                className="absolute inset-y-0 right-0 px-4 flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800"
              >
                {showPasswords ? "Hide" : "Show"}
              </button>
            </div>
            {fieldError("password_confirmation") && (
              <p className="text-red-500 text-sm mt-1">{fieldError("password_confirmation")}</p>
            )}

            {error && (
              <p className="text-red-500 text-sm mt-4 flex items-center gap-1">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-700 hover:bg-blue-800 active:bg-blue-900 disabled:bg-blue-300 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl transition mt-6 text-lg shadow-sm hover:shadow-md"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Changing password…
                </span>
              ) : (
                "Change Password"
              )}
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className="w-full text-center text-sm font-medium text-gray-400 hover:text-gray-600 mt-4 transition"
            >
              Not you? Log out
            </button>
          </form>
        </div>

        <p className="text-base text-gray-500 text-center mt-8">
          © {new Date().getFullYear()} University of Sri Jayewardenepura
        </p>
      </div>
    </AuthShell>
  );
}
